import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const badgeVariants = cva(
  // Base styles - Luxury Fintech
  [
    'inline-flex items-center gap-1.5',
    'px-2.5 py-1',
    'rounded-md',
    'text-xs font-medium tracking-wide',
    'transition-all duration-200',
  ],
  {
    variants: {
      variant: {
        // Default - subtle, borderless
        default: [
          'bg-surface-container text-text-secondary',
          'border-transparent',
        ],
        // Primary - gold accent, subtle border
        primary: [
          'bg-[rgba(201,162,39,0.12)] text-[#d4b23d]',
          'border-transparent',
        ],
        // Bullish - muted green
        bullish: [
          'bg-[rgba(34,197,94,0.15)] text-[#22c55e]',
          'border-transparent',
        ],
        // Bearish - muted red
        bearish: [
          'bg-[rgba(239,68,68,0.15)] text-[#ef4444]',
          'border-transparent',
        ],
        // Warning - warm amber
        warning: [
          'bg-[rgba(245,158,11,0.15)] text-[#f59e0b]',
          'border-transparent',
        ],
        // Info - institutional gold (neutral callouts)
        info: [
          'bg-[rgba(242,202,80,0.12)] text-[#e8c547]',
          'border-transparent',
        ],
        // Solid - filled
        solid: [
          'bg-gradient-to-r from-[#d4b23d] to-[#c9a227] text-[#0f1218]',
          'border-transparent font-semibold',
        ],
        // Outline - elegant border
        outline: [
          'bg-transparent text-text-primary',
          'border border-border-subtle',
        ],
        // Secondary - elevated background
        secondary: [
          'bg-surface-container-high text-text-secondary',
          'border-transparent',
        ],
      },
      size: {
        sm: 'text-xs px-2 py-0.5',
        md: 'text-xs px-2.5 py-1',
        lg: 'text-sm px-3 py-1.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Optional icon to display before the text */
  icon?: React.ReactNode;
  /** Optional dot indicator */
  showDot?: boolean;
  /** Dot color class */
  dotColor?: string;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, icon, showDot, dotColor, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size, className }))}
        {...props}
      >
        {showDot && (
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              dotColor || 'bg-current'
            )}
            aria-hidden="true"
          />
        )}
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
